"use client"
import {
  Badge,
  Box,
  Flex,
  Table,
  Text
} from '@radix-ui/themes';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Check,
  Clock,
  Eye,
  Filter,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  User,
  UserPlus,
  WifiOff,
  X
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ActivityItem {
  id: string;
  user: string;
  action: string;
  content: string;
  time: string;
  type: string;
  icon: string;
  color: string;
  bgColor: string;
  role?: string;
}

const iconMap = {
  Plus,
  Pencil,
  Check,
  Trash2,
  Eye,
  UserPlus
};

type ErrorType = 'network' | 'auth' | 'server' | 'timeout' | 'unknown';

interface ErrorState {
  type: ErrorType;
  message: string;
  details?: string;
  canRetry: boolean;
}

export default function RecentActivity() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorState | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [actionFilter, setActionFilter] = useState('All');
  const [showAllModal, setShowAllModal] = useState(false);
  const [allActivities, setAllActivities] = useState<ActivityItem[] | null>(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [_lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [_isOnline, setIsOnline] = useState(navigator.onLine);
  const errorTypeRef = useRef<ErrorState['type'] | null>(null);

  useEffect(() => {
    errorTypeRef.current = error?.type ?? null;
  }, [error?.type]);

  const fetchActivities = useCallback(async () => {
    const getErrorState = (error: unknown, response?: Response): ErrorState => {
      if (!navigator.onLine) {
        return {
          type: 'network',
          message: 'No internet connection',
          details: 'Please check your network connection and try again.',
          canRetry: true
        };
      }

      if (response && (response.status === 401 || response.status === 403)) {
        return {
          type: 'auth',
          message: 'Authentication required',
          details: 'Your session has expired. Please log in again.',
          canRetry: false
        };
      }

      if (response && response.status >= 500) {
        return {
          type: 'server',
          message: 'Server error',
          details: 'The server is currently experiencing issues. Please try again later.',
          canRetry: true
        };
      }

      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('timeout'))) {
        return {
          type: 'timeout',
          message: 'Request timeout',
          details: 'The request took too long to complete. Please try again.',
          canRetry: true
        };
      }

      return {
        type: 'unknown',
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        details: 'Something went wrong while loading recent activities.',
        canRetry: true
      };
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/admin/recent-activities', {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (data.success && Array.isArray(data.activities)) {
        setActivities(data.activities);
        setLastUpdated(new Date());
        setRetryCount(0);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
      const errorState = getErrorState(err, err instanceof Response ? err : undefined);
      setError(errorState);
    } finally {
      setLoading(false);
      clearTimeout(timeoutId);
    }
  }, []);

  const fetchAllActivities = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await fetch('/api/admin/recent-activities?limit=all', { headers: { 'Cache-Control': 'no-cache' } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load all activities');
      if (data.success && Array.isArray(data.activities)) {
        setAllActivities(data.activities);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (e) {
      console.error('Failed to load all activities', e);
    } finally {
      setLoadingAll(false);
    }
  }, []);

  // Parse time ago strings to timestamps for sorting
  const parseTimeAgo = (timeString: string): number => {
    const now = new Date().getTime();

    if (timeString === 'Just now') return now;
    if (timeString.includes('minutes ago')) {
      const mins = parseInt(timeString);
      return now - (mins * 60 * 1000);
    }
    if (timeString.includes('hours ago')) {
      const hours = parseInt(timeString);
      return now - (hours * 60 * 60 * 1000);
    }
    if (timeString.includes('days ago')) {
      const days = parseInt(timeString);
      return now - (days * 24 * 60 * 60 * 1000);
    }

    // For older dates, return a very old timestamp
    return 0;
  };

  // Sort and filter activities based on search term and filters
  useEffect(() => {
    // Sort activities by time (newest first)
    const sortedActivities = [...activities].sort((a, b) => {
      const timeA = parseTimeAgo(a.time);
      const timeB = parseTimeAgo(b.time);
      return timeB - timeA; // Descending order (newest first)
    });

    // Apply filters
    let filtered = sortedActivities;

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(activity =>
        activity.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (typeFilter !== 'All') {
      filtered = filtered.filter(activity => activity.type === typeFilter);
    }

    // Action filter (normalized matching)
    if (actionFilter !== 'All') {
      const normalizedFilter = actionFilter.toLowerCase();
      filtered = filtered.filter(activity => {
        const activityAction = activity.action.toLowerCase();
        // Match exact or partial (for variations like "updated" matching "update")
        return activityAction === normalizedFilter ||
          activityAction.includes(normalizedFilter) ||
          normalizedFilter.includes(activityAction);
      });
    }

     
    setFilteredActivities(filtered);
  }, [activities, searchTerm, typeFilter, actionFilter]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      fetchActivities();
    }, 0);

    // Network status monitoring
    const handleOnline = () => {
      setIsOnline(true);
      if (errorTypeRef.current === 'network') {
        fetchActivities(); // Auto retry when connection is restored
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setError({
        type: 'network',
        message: 'Connection lost',
        details: 'You appear to be offline. Activity feed will update when connection is restored.',
        canRetry: false
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchActivities]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchActivities();
  };

  const getErrorIcon = (errorType: ErrorType) => {
    switch (errorType) {
      case 'network':
        return <WifiOff className="h-5 w-5 text-red-500" />;
      case 'auth':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'server':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'timeout':
        return <Clock className="h-5 w-5 text-blue-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getErrorColor = (errorType: ErrorType) => {
    switch (errorType) {
      case 'network':
        return 'border-red-200 bg-red-50';
      case 'auth':
        return 'border-yellow-200 bg-yellow-50';
      case 'server':
        return 'border-orange-200 bg-orange-50';
      case 'timeout':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-red-200 bg-red-50';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
              <p className="text-sm text-gray-500">Your recent actions</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
              <span className="text-xs text-gray-500">Loading...</span>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="px-6 py-4">
              <div className="flex items-start space-x-3">
                <div className="p-2 rounded-full bg-gray-100 animate-pulse">
                  <div className="h-4 w-4 bg-gray-300 rounded"></div>
                </div>

                <div className="flex-1 min-w-0 space-y-2">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                  <div className="animate-pulse">
                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {/* Error Header */}
        <div className={`rounded-lg border p-4 ${getErrorColor(error.type)}`}>
          <div className="flex items-start space-x-3">
            {getErrorIcon(error.type)}
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">{error.message}</h3>
              {error.details && (
                <p className="text-sm text-gray-700 mt-1">{error.details}</p>
              )}
              {retryCount > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  Attempt {retryCount + 1} of 3
                </p>
              )}
            </div>
            {error.canRetry && (
              <button
                onClick={handleRetry}
                disabled={retryCount >= 3}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {retryCount >= 3 ? 'Max retries reached' : 'Try Again'}
              </button>
            )}
          </div>
        </div>

        {/* Activity Feed Container with Empty State */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
            <p className="text-sm text-gray-500">Your recent actions</p>
          </div>

          <div className="px-6 py-8 text-center">
            <div className="text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">Activity feed unavailable</p>
              <p className="text-sm text-gray-400">Recent activities will appear here when connection is restored</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get unique types for filters
  const uniqueTypes = Array.from(new Set((activities || []).map(a => a.type))).sort();

  // Define all possible action filters (normalized)
  const allPossibleActions = [
    'created',
    'updated',
    'edited',
    'edit',
    'upload',
    'uploaded',
    'deleted',
    'delete',
    'published',
    'publish',
    'unpublished',
    'unpublish',
    'joined',
    'modified',
    'viewed',
    'view',
    'restored',
    'restore',
    'archived',
    'archive'
  ];

  // Get unique actions from activities and merge with predefined list
  const uniqueActionsFromData = Array.from(new Set((activities || []).map(a => a.action.toLowerCase()))).sort();
  const allActions = ['All', ...Array.from(new Set([...allPossibleActions, ...uniqueActionsFromData])).sort()];

  return (
    <div className="space-y-6">
      {/* Activity Feed */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header with Search and Filters */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 border-b border-gray-100">
          <div className="flex items-center justify-between px-6 p-3 gap-4 flex-wrap">
            {/* Title Section */}
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="h-1 w-1 rounded-full bg-blue-500 animate-pulse"></div>
                <h3 className="text-xl font-medium text-gray-900 tracking-tight leading-tight">
                  Recent <span className="text-blue-600">Activity</span>
                </h3>
              </div>
              <p className="text-gray-600 text-sm ml-4">Your recent actions and updates</p>
            </div>

            {/* Search and Filters Section */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search Input */}
              <div className="relative w-96 min-w-[200px]">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none   bg-white shadow-sm"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              {/* Type Filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg  focus:border-blue-500 bg-white shadow-sm"
                >
                  <option value="All">All Types</option>
                  {uniqueTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Action Filter */}
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg  focus:border-blue-500 bg-white shadow-sm"
              >
                {allActions.map(action => {
                  // Capitalize first letter for display
                  const displayName = action === 'All' ? 'All Actions' : action.charAt(0).toUpperCase() + action.slice(1);
                  return (
                    <option key={action} value={action}>{displayName}</option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* Activity Table */}
        <div className="p-6 bg-gradient-to-b from-white to-gray-50/30">
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="mb-2 text-gray-900 font-medium">
                  {searchTerm || typeFilter !== 'All' || actionFilter !== 'All' ? 'No matching activities found' : 'No recent activities'}
                </p>
                <p className="text-sm text-gray-500">
                  {searchTerm || typeFilter !== 'All' || actionFilter !== 'All'
                    ? 'Try adjusting your search terms or filters'
                    : 'Activity feed is empty. New actions will appear here.'
                  }
                </p>
                {(searchTerm || typeFilter !== 'All' || actionFilter !== 'All') && (
                  <button
                    onClick={() => { setSearchTerm(''); setTypeFilter('All'); setActionFilter('All'); }}
                    className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>User</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Activity</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Content</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredActivities.slice(0, 5).map((activity, index) => {
                    const IconComponent = iconMap[activity.icon as keyof typeof iconMap] || Eye;
                    return (
                      <Table.Row key={activity.id} className="hover:bg-gray-50/50 transition-colors">
                        <Table.Cell>
                          <Flex gap="2" align="center">
                            {activity.role === 'Admin' ? (
                              <Shield className="h-3.5 w-3.5 text-red-600" />
                            ) : activity.role === 'Editor' ? (
                              <Pencil className="h-3.5 w-3.5 text-blue-600" />
                            ) : (
                              <User className="h-3.5 w-3.5 text-green-600" />
                            )}
                            <Text size="2" className="text-gray-900 font-medium">{activity.user}</Text>
                          </Flex>
                        </Table.Cell>
                        <Table.Cell>
                          <Flex gap="2" align="center">
                            <div className={`p-1 rounded-lg ${activity.bgColor} flex-shrink-0 shadow-sm`}>
                              <IconComponent className={`h-3.5 w-3.5 ${activity.color}`} />
                            </div>
                            <Text size="2" className="text-gray-600">{activity.action}</Text>
                            {index === 0 && (
                              <Badge variant="soft" color="blue" className="text-xs px-1.5 py-0.5">
                                Latest
                              </Badge>
                            )}
                          </Flex>
                        </Table.Cell>
                        <Table.Cell>
                          <Text size="2" weight="medium" className="text-gray-500 truncate max-w-md">
                            {activity.content}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Flex gap="1" align="center">
                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                            <Text size="2" className="text-gray-500">{activity.time}</Text>
                          </Flex>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge variant="soft" color="gray" className="text-xs">
                            {activity.type}
                          </Badge>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Root>

              {/* Footer */}
              <Box className="px-0 py-4 ">
                <Flex justify="between" align="center">
                  <button
                    onClick={() => { setShowAllModal(true); if (!allActivities) fetchAllActivities(); }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    View all activity →
                  </button>
                  <Text size="2" className="text-gray-500">
                    Showing {Math.min(5, filteredActivities.length)} of {activities.length} activities
                  </Text>
                </Flex>
              </Box>
            </>
          )}
        </div>
      </div>

      {/* Modal for All Activities */}
      {showAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowAllModal(false)}></div>
          <div className="relative bg-white w-full max-w-3xl mx-4 rounded-lg shadow-lg border border-gray-200 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">All Activity</h3>
                <p className="text-sm text-gray-500">Most recent events across content and users</p>
              </div>
              <button onClick={() => setShowAllModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingAll ? (
                <div className="p-6 text-sm text-gray-500">Loading all activities…</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {(allActivities || activities).map((activity) => {
                    const IconComponent = iconMap[activity.icon as keyof typeof iconMap] || Eye;
                    return (
                      <div key={`all_${activity.id}`} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-full ${activity.bgColor} flex-shrink-0`}>
                            <IconComponent className={`h-4 w-4 ${activity.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                {activity.user}
                                {activity.role && (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100" title={activity.role}>
                                    {activity.role === 'Admin' ? (
                                      <Shield className="h-3 w-3 text-red-600" />
                                    ) : activity.role === 'Editor' ? (
                                      <Pencil className="h-3 w-3 text-blue-600" />
                                    ) : (
                                      <User className="h-3 w-3 text-green-600" />
                                    )}
                                  </span>
                                )}
                              </span>
                              <span className="text-sm text-gray-500">{activity.action}</span>
                              <span className="text-sm text-gray-500 break-words">{activity.content}</span>
                            </div>
                            <div className="mt-1 text-xs text-gray-500">{activity.time} • {activity.type}</div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end">
              <button onClick={() => setShowAllModal(false)} className="text-sm text-blue-600 hover:text-blue-800">Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
